import apiClient from "@/api/client";

export type AlertSeverity = "info" | "warning" | "critical";
export type AlertOperator = "<" | "<=" | ">" | ">=" | "==";

export const ALERT_METRICS = [
  "node.ram_pct_free",
  "node.disk_pct_free",
  "node.cpu_pct_free",
  "vm.cpu_pct",
  "vm.mem_pct",
] as const;
export type AlertMetric = (typeof ALERT_METRICS)[number];

export interface AlertRule {
  id: number;
  name: string;
  metric: AlertMetric;
  operator: AlertOperator;
  threshold: number;
  severity: AlertSeverity;
  scope: string | null;
  enabled: boolean;
  notify_ntfy: boolean;
  created_at: string;
  updated_at: string;
}

export interface AlertRuleCreate {
  name: string;
  metric: AlertMetric;
  operator: AlertOperator;
  threshold: number;
  severity?: AlertSeverity;
  scope?: string | null;
  enabled?: boolean;
  notify_ntfy?: boolean;
}

export async function listAlertRules(): Promise<AlertRule[]> {
  const { data } = await apiClient.get<AlertRule[]>("/monitoring/rules");
  return data;
}

export async function createAlertRule(
  payload: AlertRuleCreate,
): Promise<AlertRule> {
  const { data } = await apiClient.post<AlertRule>("/monitoring/rules", payload);
  return data;
}

export async function updateAlertRule(
  id: number,
  payload: Partial<AlertRuleCreate>,
): Promise<AlertRule> {
  const { data } = await apiClient.patch<AlertRule>(
    `/monitoring/rules/${id}`,
    payload,
  );
  return data;
}

export async function deleteAlertRule(id: number): Promise<void> {
  await apiClient.delete(`/monitoring/rules/${id}`);
}

// ── Persisted alerts ─────────────────────────────────────────────────────────

export interface PersistedAlert {
  id: number;
  rule_id: number | null;
  severity: string;
  title: string;
  message: string;
  source: string | null;
  metric_value: number | null;
  status: "active" | "acknowledged" | "resolved";
  acknowledged_at: string | null;
  acknowledged_by: string | null;
  resolved_at: string | null;
  created_at: string;
}

export async function listPersistedAlerts(
  status?: PersistedAlert["status"],
): Promise<PersistedAlert[]> {
  const params = status ? { status } : {};
  const { data } = await apiClient.get<PersistedAlert[]>(
    "/monitoring/alerts/db",
    { params },
  );
  return data;
}

export async function ackPersistedAlert(id: number): Promise<PersistedAlert> {
  const { data } = await apiClient.post<PersistedAlert>(
    `/monitoring/alerts/db/${id}/acknowledge`,
  );
  return data;
}

export async function resolvePersistedAlert(id: number): Promise<PersistedAlert> {
  const { data } = await apiClient.post<PersistedAlert>(
    `/monitoring/alerts/db/${id}/resolve`,
  );
  return data;
}
